/* Seed template for a brand-new (unconfigured) custom-request policy. Rendered when no
   policy row exists yet (exists=false) so the operator starts from a sensible, editable
   template instead of blank fields — priceable custom types, delivery windows, and the
   strategy / pricing-guidance prompts. Nothing is persisted until the operator saves, and
   the mode stays 'handoff' (the missing-row default) so seeding a template never silently
   switches the account into automation. Also feeds the "Load examples" button so both
   share one source of truth. */
import type { CustomPolicyBody, CustomPricings } from '../../../api/client';

type Translate = (key: string) => string;

// Prices are language-agnostic; the label (the dict key the matching LLM reads and the
// operator sees) is translated via the customRequests namespace.
const CUSTOM_TYPES: Array<{ key: string; price: number; floor: number }> = [
  { key: 'defaults.types.photoSet', price: 40, floor: 25 },
  { key: 'defaults.types.solo5', price: 90, floor: 60 },
  { key: 'defaults.types.solo10', price: 160, floor: 110 },
  { key: 'defaults.types.personalised', price: 30, floor: 20 },
  { key: 'defaults.types.gg', price: 200, floor: 150 },
];

export function defaultCustomPricings(t: Translate): CustomPricings {
  const out: CustomPricings = {};
  for (const e of CUSTOM_TYPES) {
    const name = t(e.key).trim();
    if (name) out[name] = { price: e.price, floor: e.floor };
  }
  return out;
}

export function defaultCustomBody(t: Translate): CustomPolicyBody {
  return {
    is_active: true,
    // Template only — never auto-enable automation just because we pre-filled fields.
    // Matches the backend's missing-row default ('handoff'); the operator opts into automate.
    custom_request_mode: 'handoff',
    custom_pricings: defaultCustomPricings(t),
    photo_delivery_eta: t('defaults.photoEta'),
    video_delivery_eta: t('defaults.videoEta'),
    tip_tolerance: 2,
    max_negotiation_attempts: 3,
    strategy_prompt: t('defaults.strategyPrompt'),
    pricing_guidance: t('defaults.pricingGuidance'),
    // Only consulted in 'decline' mode; seeded empty so a fresh template stays neutral.
    decline_note: null,
  };
}
